import { Phone, FileText, Hammer, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

const Process = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: t('process.step1.number'),
      title: t('process.step1.title'),
      description: t('process.step1.desc'),
      icon: Phone
    },
    {
      number: t('process.step2.number'),
      title: t('process.step2.title'),
      description: t('process.step2.desc'),
      icon: FileText
    },
    {
      number: t('process.step3.number'),
      title: t('process.step3.title'),
      description: t('process.step3.desc'),
      icon: Hammer
    },
    {
      number: t('process.step4.number'),
      title: t('process.step4.title'),
      description: t('process.step4.desc'),
      icon: CheckCircle2
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden"
      data-testid="process-section"
    >
      <style>{`
        @keyframes processFadeUp {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes processLineGrow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes processIconPop {
          0%   { transform: scale(0) rotate(-180deg); opacity: 0; }
          60%  { transform: scale(1.15) rotate(10deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes processIconPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(26, 122, 138, 0.4); }
          50%      { box-shadow: 0 0 0 14px rgba(26, 122, 138, 0); }
        }
        @keyframes processNumberShimmer {
          0%, 100% { background-position: 0% center; }
          50%      { background-position: 100% center; }
        }
        @keyframes processHeadingReveal {
          from { opacity: 0; letter-spacing: -0.02em; transform: translateY(20px); }
          to   { opacity: 1; letter-spacing: 0; transform: translateY(0); }
        }
        @keyframes processHeadingUnderline {
          0%   { width: 0; opacity: 0; }
          60%  { width: 90px; opacity: 1; }
          100% { width: 70px; opacity: 1; }
        }
        .process-card {
          opacity: 0;
          animation: processFadeUp 0.7s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
        }
        .process-card:hover {
          transform: translateY(-8px);
        }
        .process-card:hover .process-icon {
          transform: scale(1.12) rotate(-8deg);
        }
        .process-card:hover .process-number {
          color: rgba(26, 122, 138, 0.25);
        }
        .process-icon {
          transition: transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
          animation: processIconPulse 2.6s ease-in-out infinite;
        }
        .process-card.is-active .process-icon-inner {
          animation: processIconPop 0.7s cubic-bezier(0.22, 0.61, 0.36, 1) backwards;
        }
        .process-number {
          background: linear-gradient(90deg, #e5e7eb 0%, #cbd5e1 50%, #e5e7eb 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: processNumberShimmer 5s ease-in-out infinite;
          transition: color 0.4s ease;
        }
        .process-line {
          transform: scaleX(0);
          transform-origin: left center;
          animation: processLineGrow 1.6s 0.4s cubic-bezier(0.65, 0, 0.35, 1) forwards;
        }
        .process-heading {
          animation: processHeadingReveal 0.8s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
        }
        .process-heading-underline {
          animation: processHeadingUnderline 1.2s 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
        }
      `}</style>

      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 ${inView ? 'process-heading' : 'opacity-0'}`}
          >
            {t('process.title')}
          </h2>
          <div className="flex justify-center mb-4">
            <div
              className={`h-1 bg-gradient-to-r from-[#3da5ba] to-[#1a7a8a] rounded-full ${inView ? 'process-heading-underline' : 'w-0'}`}
              style={{ width: 0 }}
            />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('process.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Animated connecting line for desktop */}
          {inView && (
            <div
              className="hidden lg:block absolute top-24 left-0 right-0 h-1 mx-16 rounded-full process-line bg-gradient-to-r from-[#3da5ba] via-[#1a7a8a] to-[#3da5ba]"
              style={{ zIndex: 0 }}
            />
          )}

          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={step.number}
                className={`relative ${inView ? 'process-card is-active' : 'opacity-0'}`}
                style={{
                  zIndex: 1,
                  animationDelay: `${idx * 0.15}s`,
                }}
              >
                <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 h-full border-t-4 border-[#1a7a8a]">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-5xl font-bold process-number">
                      {step.number}
                    </span>
                    <div className="process-icon inline-flex items-center justify-center w-14 h-14 bg-[#1a7a8a] rounded-full">
                      <div
                        className="process-icon-inner inline-flex items-center justify-center"
                        style={{ animationDelay: `${0.4 + idx * 0.15}s` }}
                      >
                        <Icon size={28} className="text-white" />
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Process;
